"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { MapPin, Mail, Phone, Clock, ExternalLink, Loader } from "lucide-react";
const PRACTICE_AREAS = ["Oil Change & Filter", "Brake Inspection", "Engine Diagnostics", "Tire Rotation", "Transmission Repair", "General Maintenance", "NY State Inspection"];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-muted/50 p-8 text-center">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="mb-2 font-default text-xl font-bold uppercase tracking-tight text-foreground">
          Appointment Requested!
        </h3>
        <p className="mb-6 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary/5 rounded-full font-bold uppercase">
          Schedule Another Vehicle
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-sm font-bold uppercase tracking-wider text-foreground">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className="rounded-md border-border focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormDescription className="text-xs">Enter the name for the service record.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-sm font-bold uppercase tracking-wider text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="rounded-md border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs">For booking confirmation.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-sm font-bold uppercase tracking-wider text-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(518) 555-0123" className="rounded-md border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs">
                Best number for status updates.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="practice_area" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-sm font-bold uppercase tracking-wider text-foreground">Service Needed</FormLabel>
            <FormControl>
              <select className="border-border focus-visible:border-primary focus-visible:ring-primary/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-sm transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" {...field}>
                <option value="" disabled>
                  Select the type of service
                </option>
                {PRACTICE_AREAS.map((area, i) => <option key={area} value={area} data-index={i}>
                    {area}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Vehicle details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-sm font-bold uppercase tracking-wider text-foreground">Vehicle Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Year, Make, Model and any specific issues..." className="min-h-[120px] resize-y rounded-md border-border focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 md:w-auto md:px-12 font-default font-bold uppercase tracking-widest shadow-md transition-all rounded-full" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "Secure Your Slot"}
      </Button>
    </Form>;
}
export default function FaleComEspecialistas() {
  const contactBlocks = [{
    icon: <Phone className="size-6 text-primary-foreground" />,
    title: "Call the Shop",
    content: "(518) 459-3404",
    action: <Link to="tel:5184651234" className="mt-2 inline-flex items-center text-sm font-bold uppercase tracking-tight text-primary hover:underline">
          Call Now <ExternalLink className="ml-1 size-3" />
        </Link>
  }, {
    icon: <Mail className="size-6 text-primary-foreground" />,
    title: "Service Inquiries",
    content: "service@dottsgarage.com",
    action: <Link to="mailto:service@dottsgarage.com" className="mt-2 inline-flex items-center text-sm font-bold uppercase tracking-tight text-primary hover:underline">
          Email Us <ExternalLink className="ml-1 size-3" />
        </Link>
  }, {
    icon: <Clock className="size-6 text-primary-foreground" />,
    title: "Garage Hours",
    content: "Mon - Fri:\xA0 8 AM - 4 PM",
    action: null
  }, {
    icon: <MapPin className="size-6 text-primary-foreground" />,
    title: "Our Location",
    content: "1177 Central Ave, Albany, NY 12205",
    action: <Link to="https://maps.google.com/?q=1177+Central+Ave,+Albany,+NY,+12205" newTab className="mt-3 inline-flex items-center justify-center rounded-md border border-primary/20 bg-white px-4 py-2 text-sm font-bold uppercase tracking-tight text-primary shadow-sm transition-colors hover:bg-accent">
          Get Directions
        </Link>
  }];
  return <section data-section-id="224539" id="secure-your-slot" className="bg-background text-foreground py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center lg:text-left">
          <h2 className="font-default text-4xl font-black uppercase tracking-tighter text-foreground sm:text-5xl lg:text-7xl">
            Ready for a Checkup?
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground lg:mx-auto text-center font-default">&nbsp;Dott's Garage has been Albany's trusted mechanic for over 100 years. Book your appointment today and experience century-old reliability paired with modern automotive expertise.</p>
        </div>

        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
          {}
          <div className="rounded-lg border-2 border-primary bg-card p-6 shadow-lg sm:p-8">
            <FormProvider formKey="appointment_form_home" formId="appointment_form_home" sectionName="Secure Your Slot" defaultValues={{
            name: "",
            email: "",
            phone: "",
            practice_area: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true
            },
            phone: {
              required: true
            },
            practice_area: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Secure Your Slot" successMessage="Your request has been received! Our service advisor will contact you shortly to confirm your time.">
              <ContactFormInner />
            </FormProvider>
          </div>

          {}
          <div className="flex flex-col justify-center space-y-14">
            {contactBlocks.map((block, i) => <div key={i} data-index={i} className="flex items-start gap-6">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-md bg-primary border border-primary shadow-md">
                  {block.icon}
                </div>
                <div>
                  <h3 className="font-default text-lg font-bold uppercase tracking-tight text-foreground">
                    {block.title}
                  </h3>
                  <p className="mt-1 text-base text-muted-foreground font-default">
                    {block.content}
                  </p>
                  {block.action}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}